/**
 * LibraryPage — the "Digital Bill Binder" (Sprint 2026-09)
 *
 * Physical-file-folder metaphor: every bill, expense, and vendor ledger the
 * user has ever attached lives here, organized behind divider tabs.
 *   ┌───────────────────────────────────────────────────────────────┐
 *   │ 🗂  Library      [By Vendor] [By Month]        [Export PDF ⬇] │
 *   ├───────────────────────────────────────────────────────────────┤
 *   │ SHIV GAURI     (12 bills · ₹4.2L)                             │
 *   │  ┌───────┐  ┌───────┐  ┌───────┐  ┌───────┐                   │
 *   │  │thumb  │  │thumb  │  │thumb  │  │thumb  │                   │
 *   │  │01 Nov │  │12 Oct │  │30 Sep │  │15 Sep │  ...              │
 *   │  │₹34,200│  │₹28,900│  │...    │  │...    │                   │
 *   │  └───────┘  └───────┘  └───────┘  └───────┘                   │
 *   │ ────────────────────────────────────────────────────────────  │
 *   │ VADILAL        (8 bills · ₹1.9L)                              │
 *   │  ...                                                          │
 *   └───────────────────────────────────────────────────────────────┘
 *
 * View modes
 *   • By Vendor  — sections keyed by vendor_key (bills + ledgers per vendor)
 *   • By Month   — sections keyed by YYYY-MM (bills + expenses + ledgers)
 *
 * Cards
 *   • Bill    — thumbnail + vendor + date + total → click → /bills/:id
 *   • Ledger  — file icon + vendor + period + closing balance → click → download
 *   • Expense — receipt icon + category + amount + note (month view only)
 *
 * Export
 *   • "Export folder" streams a merged PDF of all bills+ledgers matching the
 *     current filter — one file the CA can print or archive.
 *
 * Search + Period filter live at the top; changing either re-fetches.
 * Loading uses Skeleton cards; empty state is a friendly hint.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    BookOpen,
    Search,
    Download,
    Layers,
    Calendar,
    FileText,
    Receipt,
    Wallet,
    ChevronRight,
    Loader2,
    FolderOpen,
    ImageOff,
    FileArchive,
    ShieldAlert,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DuplicatesDialog } from '../components/DuplicatesDialog';
import { toast } from 'sonner';
import { PageHeader } from '../components/PageHeader';
import { PeriodPicker, useSharedPeriod, periodToParams } from '../components/PeriodPicker';
import { api, fmtMoney, fmtDate, API, getToken } from '../lib/api';

// ---------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------
const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

function monthLabel(ym) {
    // "2026-01" → "January 2026"
    if (!ym || ym.length < 7) return ym || 'Undated';
    const [y, m] = ym.split('-');
    const idx = Math.max(0, Math.min(11, Number(m) - 1));
    return `${MONTH_NAMES[idx]} ${y}`;
}

function monthOf(iso) {
    if (!iso || typeof iso !== 'string' || iso.length < 7) return 'undated';
    return iso.slice(0, 7);
}

function formatBytes(b) {
    if (!b || b < 1024) return `${b || 0} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

// ---------------------------------------------------------------
// Card sub-components
// ---------------------------------------------------------------
function BillCard({ bill }) {
    const thumb = bill.thumbnail;
    return (
        <Link
            to={`/bills/${bill.id}`}
            className="group block"
            data-testid={`library-bill-card-${bill.id}`}
        >
            <div className="relative aspect-[3/4] rounded-lg border border-border bg-muted overflow-hidden shadow-sm transition-all group-hover:shadow-md group-hover:-translate-y-0.5 group-hover:border-primary/40">
                {/* Punch-hole styling — two soft dots on the left edge so the card
                    feels like a page in a physical binder. Purely decorative. */}
                <div className="pointer-events-none absolute left-1.5 top-4 w-1.5 h-1.5 rounded-full bg-background/80 shadow-inner z-10" />
                <div className="pointer-events-none absolute left-1.5 bottom-4 w-1.5 h-1.5 rounded-full bg-background/80 shadow-inner z-10" />
                {thumb ? (
                    <img
                        src={thumb}
                        alt={`${bill.vendor} bill`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                    />
                ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground bg-gradient-to-b from-muted to-secondary">
                        <FileText size={32} strokeWidth={1.4} />
                        <span className="mt-2 text-[10px] uppercase tracking-wider">No preview</span>
                    </div>
                )}
                {bill.needs_review && (
                    <Badge
                        variant="secondary"
                        className="absolute top-2 right-2 bg-amber-100 text-amber-900 border border-amber-300 text-[9px] uppercase px-1.5 py-0"
                    >
                        Review
                    </Badge>
                )}
            </div>
            <div className="mt-2 space-y-0.5">
                <div className="text-[13px] font-medium leading-snug truncate">{bill.vendor}</div>
                <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                    <span>{fmtDate(bill.bill_date) || '—'}</span>
                    <span className="font-medium text-foreground">{fmtMoney(bill.total)}</span>
                </div>
                {bill.invoice_number && (
                    <div className="text-[10px] text-muted-foreground truncate">
                        #{bill.invoice_number}
                    </div>
                )}
            </div>
        </Link>
    );
}

function LedgerCard({ ledger }) {
    // Ledger cards live alongside bills in the vendor's section.
    // Clicking downloads the original PDF via authenticated fetch.
    const [busy, setBusy] = useState(false);
    async function handleOpen() {
        try {
            setBusy(true);
            const token = getToken();
            const r = await fetch(
                `${API}/vendors/${encodeURIComponent(ledger.vendor_key)}/ledgers/${ledger.id}/file`,
                { headers: { Authorization: `Bearer ${token}` } },
            );
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const blob = await r.blob();
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank', 'noopener');
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (e) {
            toast.error('Could not open ledger', { description: String(e.message || e) });
        } finally {
            setBusy(false);
        }
    }
    return (
        <button
            type="button"
            onClick={handleOpen}
            className="group block w-full text-left"
            data-testid={`library-ledger-card-${ledger.id}`}
        >
            <div className="relative aspect-[3/4] rounded-lg border border-border bg-gradient-to-b from-secondary/60 to-muted overflow-hidden shadow-sm transition-all group-hover:shadow-md group-hover:-translate-y-0.5 group-hover:border-primary/40 flex flex-col items-center justify-center gap-2 px-3">
                <div className="pointer-events-none absolute left-1.5 top-4 w-1.5 h-1.5 rounded-full bg-background/80 shadow-inner" />
                <div className="pointer-events-none absolute left-1.5 bottom-4 w-1.5 h-1.5 rounded-full bg-background/80 shadow-inner" />
                <FileText size={40} strokeWidth={1.2} className="text-primary" />
                <span className="text-[9px] uppercase tracking-widest text-muted-foreground">Vendor Ledger</span>
                {busy && (
                    <div className="absolute inset-0 bg-background/70 flex items-center justify-center">
                        <Loader2 size={18} className="animate-spin text-primary" />
                    </div>
                )}
            </div>
            <div className="mt-2 space-y-0.5">
                <div className="text-[13px] font-medium leading-snug truncate">{ledger.vendor}</div>
                <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                    <span>{fmtDate(ledger.uploaded_at) || '—'}</span>
                    <span>{formatBytes(ledger.size_bytes)}</span>
                </div>
                {ledger.period_from && (
                    <div className="text-[10px] text-muted-foreground truncate">
                        {ledger.period_from} → {ledger.period_to || '—'}
                    </div>
                )}
            </div>
        </button>
    );
}

function ExpenseCard({ expense }) {
    return (
        <div
            className="group block"
            data-testid={`library-expense-card-${expense.id}`}
        >
            <div className="aspect-[3/4] rounded-lg border border-dashed border-border bg-gradient-to-b from-amber-50/40 to-transparent dark:from-amber-950/10 overflow-hidden shadow-sm flex flex-col items-center justify-center gap-2 px-3">
                <Wallet size={38} strokeWidth={1.2} className="text-amber-600" />
                <span className="text-[9px] uppercase tracking-widest text-muted-foreground">Expense</span>
                {expense.kind_of && (
                    <Badge
                        variant="outline"
                        className="text-[9px] uppercase px-1.5 py-0 border-amber-300 text-amber-800 dark:text-amber-200"
                    >
                        {expense.kind_of}
                    </Badge>
                )}
            </div>
            <div className="mt-2 space-y-0.5">
                <div className="text-[13px] font-medium leading-snug truncate">
                    {expense.category || 'Expense'}
                </div>
                <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                    <span>{fmtDate(expense.expense_date) || '—'}</span>
                    <span className="font-medium text-foreground">{fmtMoney(expense.amount)}</span>
                </div>
                {expense.description && (
                    <div className="text-[10px] text-muted-foreground truncate" title={expense.description}>
                        {expense.description}
                    </div>
                )}
            </div>
        </div>
    );
}

// ---------------------------------------------------------------
// Divider section — a "tab" in the physical binder
// ---------------------------------------------------------------
function DividerSection({ id, title, subtitle, chip, children, count, trailing }) {
    const [open, setOpen] = useState(true);
    return (
        <section
            id={id}
            className="scroll-mt-24 mb-8"
            data-testid={`library-section-${id}`}
        >
            <div className="w-full flex items-center gap-3 mb-3">
                <button
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    className="flex items-center gap-3 group text-left flex-1 min-w-0"
                    aria-expanded={open}
                    data-testid={`library-section-toggle-${id}`}
                >
                    <ChevronRight
                        size={16}
                        className={`text-muted-foreground transition-transform shrink-0 ${open ? 'rotate-90' : ''}`}
                    />
                    <h3 className="text-base font-semibold tracking-tight text-foreground truncate">{title}</h3>
                    {chip && (
                        <Badge
                            variant="secondary"
                            className="ml-1 rounded-full bg-primary/10 text-primary border-primary/20 hover:bg-primary/10 text-[10px] uppercase tracking-wider whitespace-nowrap"
                        >
                            {chip}
                        </Badge>
                    )}
                    <span className="text-[11px] text-muted-foreground ml-auto whitespace-nowrap">
                        {count} {count === 1 ? 'item' : 'items'}
                    </span>
                    <div className="flex-1 hidden sm:block border-b border-dashed border-border/70 h-1 self-end mb-1" />
                </button>
                {trailing && <div className="shrink-0">{trailing}</div>}
            </div>
            {subtitle && (
                <div className="text-xs text-muted-foreground mb-3 ml-6">{subtitle}</div>
            )}
            {open && (
                <div className="ml-0 sm:ml-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4">
                    {children}
                </div>
            )}
        </section>
    );
}

// ---------------------------------------------------------------
// ExportMenu — reusable dropdown for "Merge into PDF" vs "Separate ZIP"
// Used both at the page-header level (for the whole filtered library)
// AND on each divider section (per-vendor or per-month download).
// ---------------------------------------------------------------
function ExportMenu({ exporting, disabled, onExport, label = 'Export', variant = 'outline', size = 'sm', testidPrefix }) {
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    variant={variant}
                    size={size === 'xs' ? 'sm' : size}
                    disabled={exporting || disabled}
                    className={size === 'xs' ? 'h-7 px-2 text-xs' : undefined}
                    data-testid={`${testidPrefix}-menu-trigger`}
                >
                    {exporting ? (
                        <Loader2 size={size === 'xs' ? 12 : 14} className="animate-spin mr-1.5" />
                    ) : (
                        <Download size={size === 'xs' ? 12 : 14} className="mr-1.5" />
                    )}
                    {label}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    Download format
                </DropdownMenuLabel>
                <DropdownMenuItem
                    onSelect={() => onExport('pdf')}
                    data-testid={`${testidPrefix}-menu-pdf`}
                    className="flex-col items-start gap-0.5"
                >
                    <div className="flex items-center gap-2 w-full">
                        <FileText size={14} className="text-primary" />
                        <span className="font-medium">One merged PDF</span>
                    </div>
                    <span className="text-[11px] text-muted-foreground pl-6">
                        All bills stitched into a single file (best for CA / archive)
                    </span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                    onSelect={() => onExport('zip')}
                    data-testid={`${testidPrefix}-menu-zip`}
                    className="flex-col items-start gap-0.5"
                >
                    <div className="flex items-center gap-2 w-full">
                        <FileArchive size={14} className="text-primary" />
                        <span className="font-medium">Separate files (ZIP)</span>
                    </div>
                    <span className="text-[11px] text-muted-foreground pl-6">
                        Each bill as its own file, named <code className="text-[10px]">YYYY-MM-DD_SUP_INV.pdf</code>
                    </span>
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

// ---------------------------------------------------------------
// Main page
// ---------------------------------------------------------------
export default function LibraryPage() {
    const nav = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState('');
    const [debouncedQ, setDebouncedQ] = useState('');
    const [period, setPeriod] = useSharedPeriod('this-fy');
    const [view, setView] = useState('vendor'); // 'vendor' | 'month'
    const [exporting, setExporting] = useState(false);
    const [duplicatesOpen, setDuplicatesOpen] = useState(false);
    const [includeKinds, setIncludeKinds] = useState({
        bills: true,
        expenses: true,
        ledgers: true,
    });

    // Debounce search input so we don't hammer the endpoint on every keystroke.
    useEffect(() => {
        const t = setTimeout(() => setDebouncedQ(q.trim()), 350);
        return () => clearTimeout(t);
    }, [q]);

    const activeKinds = useMemo(() => {
        const ks = [];
        if (includeKinds.bills) ks.push('bills');
        if (includeKinds.expenses) ks.push('expenses');
        if (includeKinds.ledgers) ks.push('ledgers');
        return ks.join(',');
    }, [includeKinds]);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const periodParams = periodToParams(period);
            const params = {
                ...periodParams,
                kinds: activeKinds || 'bills',
                with_thumbnails: true,
                limit: 1000,
            };
            if (debouncedQ) params.q = debouncedQ;
            const res = await api.getLibrary(params);
            setData(res);
        } catch (e) {
            toast.error('Failed to load library', { description: e?.response?.data?.detail || String(e) });
            setData({ bills: [], expenses: [], ledgers: [], vendor_summary: [], month_summary: [], counts: {} });
        } finally {
            setLoading(false);
        }
    }, [period, debouncedQ, activeKinds]);

    useEffect(() => { load(); }, [load]);

    // Group items into sections based on the current view mode.
    const sections = useMemo(() => {
        if (!data) return [];
        const bills = data.bills || [];
        const expenses = data.expenses || [];
        const ledgers = data.ledgers || [];

        if (view === 'vendor') {
            // Bills + Ledgers grouped by vendor_key. Expenses are collected
            // into a synthetic "General Expenses" divider so they don't
            // disappear entirely — expenses simply aren't vendor-scoped.
            const map = new Map();
            const push = (key, name, item) => {
                if (!map.has(key)) map.set(key, { key, name, bills: [], ledgers: [], expenses: [], lastActivity: '' });
                const bucket = map.get(key);
                if (item.kind === 'bill') bucket.bills.push(item);
                else if (item.kind === 'ledger') bucket.ledgers.push(item);
                else if (item.kind === 'expense') bucket.expenses.push(item);
                const act = item.bill_date || item.uploaded_at || item.expense_date || '';
                if (act > bucket.lastActivity) bucket.lastActivity = act;
            };
            bills.forEach((b) => push(b.vendor_key || 'unknown', b.vendor || 'Unknown vendor', b));
            ledgers.forEach((l) => push(l.vendor_key || 'unknown', l.vendor || l.vendor_key || 'Unknown vendor', l));
            if (expenses.length) {
                expenses.forEach((e) => push('__expenses__', 'General Expenses', e));
            }
            // Sort so the most recently active vendor floats to top.
            return Array.from(map.values()).sort((a, b) => {
                if (a.key === '__expenses__') return 1; // expenses divider always last
                if (b.key === '__expenses__') return -1;
                return (b.lastActivity || '').localeCompare(a.lastActivity || '');
            });
        }

        // ---- By Month ----
        const map = new Map();
        const push = (key, item) => {
            if (!map.has(key)) map.set(key, { key, name: monthLabel(key), bills: [], ledgers: [], expenses: [] });
            const bucket = map.get(key);
            if (item.kind === 'bill') bucket.bills.push(item);
            else if (item.kind === 'ledger') bucket.ledgers.push(item);
            else if (item.kind === 'expense') bucket.expenses.push(item);
        };
        bills.forEach((b) => push(monthOf(b.bill_date || b.uploaded_at), b));
        ledgers.forEach((l) => push(monthOf(l.uploaded_at), l));
        expenses.forEach((e) => push(monthOf(e.expense_date), e));
        return Array.from(map.values()).sort((a, b) => b.key.localeCompare(a.key));
    }, [data, view]);

    async function downloadExport({ format, scope }) {
        // format: 'pdf' (merged) | 'zip' (separate files)
        // scope:  { vendor_key?, month?, ...periodParams } — passed to backend
        try {
            setExporting(true);
            const payload = {
                include_bills: includeKinds.bills,
                include_ledgers: includeKinds.ledgers,
                ...scope,
            };
            const resp =
                format === 'zip'
                    ? await api.exportLibraryZip(payload)
                    : await api.exportLibraryFolder(payload);
            const blob = resp.data;
            const merged = Number(
                resp.headers?.['x-docs-merged'] ?? resp.headers?.['x-docs-zipped'] ?? 0,
            );
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            const dispo = resp.headers?.['content-disposition'] || '';
            const m = dispo.match(/filename="([^"]+)"/);
            const defaultName = format === 'zip'
                ? `library-${Date.now()}.zip`
                : `library-${Date.now()}.pdf`;
            a.href = url;
            a.download = m?.[1] || defaultName;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 60000);
            toast.success(
                format === 'zip' ? 'ZIP downloaded' : 'Merged PDF downloaded',
                {
                    description:
                        `${merged || '?'} document${merged === 1 ? '' : 's'} ${format === 'zip' ? 'in the archive' : 'merged into one PDF'}.`,
                },
            );
        } catch (e) {
            const msg = e?.response?.data?.detail || e?.message || 'Export failed';
            toast.error('Nothing to export', { description: String(msg) });
        } finally {
            setExporting(false);
        }
    }

    // Convenience shim so the top-level "Export" menu can call downloadExport
    // with the *global* filter scope in a single expression.
    function scopeForTopLevel() {
        return { ...periodToParams(period) };
    }

    const totalCount = data?.counts?.total ?? 0;
    const hasAnything = totalCount > 0;

    return (
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6" data-testid="library-page">
            <PageHeader
                icon={BookOpen}
                title="Library"
                subtitle="Your digital bill binder — every bill, expense and ledger, filed by vendor or by month."
                actions={
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDuplicatesOpen(true)}
                            data-testid="library-find-duplicates-btn"
                        >
                            <ShieldAlert size={14} className="mr-1.5" />
                            Find duplicates
                        </Button>
                        <ExportMenu
                            exporting={exporting}
                            disabled={!hasAnything}
                            onExport={(format) => downloadExport({ format, scope: scopeForTopLevel() })}
                            label="Export folder"
                            testidPrefix="library-export"
                        />
                    </div>
                }
            />

            {/* Controls row */}
            <Card className="p-3 sm:p-4 mb-6" data-testid="library-controls">
                <div className="flex flex-wrap items-center gap-3">
                    {/* Search */}
                    <div className="relative flex-1 min-w-[220px] max-w-md">
                        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search vendor, invoice #, item, category…"
                            className="pl-8 h-9"
                            data-testid="library-search"
                        />
                    </div>

                    {/* Period */}
                    <PeriodPicker value={period} onChange={setPeriod} testid="library-period" />

                    {/* View toggle */}
                    <Tabs value={view} onValueChange={setView}>
                        <TabsList data-testid="library-view-tabs">
                            <TabsTrigger value="vendor" data-testid="library-view-vendor">
                                <Layers size={13} className="mr-1.5" />
                                By Vendor
                            </TabsTrigger>
                            <TabsTrigger value="month" data-testid="library-view-month">
                                <Calendar size={13} className="mr-1.5" />
                                By Month
                            </TabsTrigger>
                        </TabsList>
                    </Tabs>

                    {/* Kind chips — click to toggle */}
                    <div className="flex items-center gap-1.5 ml-auto flex-wrap">
                        <KindChip
                            label="Bills"
                            icon={Receipt}
                            active={includeKinds.bills}
                            onClick={() => setIncludeKinds((k) => ({ ...k, bills: !k.bills }))}
                            testid="library-kind-bills"
                            count={data?.counts?.bills}
                        />
                        <KindChip
                            label="Expenses"
                            icon={Wallet}
                            active={includeKinds.expenses}
                            onClick={() => setIncludeKinds((k) => ({ ...k, expenses: !k.expenses }))}
                            testid="library-kind-expenses"
                            count={data?.counts?.expenses}
                        />
                        <KindChip
                            label="Ledgers"
                            icon={FileText}
                            active={includeKinds.ledgers}
                            onClick={() => setIncludeKinds((k) => ({ ...k, ledgers: !k.ledgers }))}
                            testid="library-kind-ledgers"
                            count={data?.counts?.ledgers}
                        />
                    </div>
                </div>
            </Card>

            {/* Content */}
            {loading ? (
                <LibrarySkeleton />
            ) : !hasAnything ? (
                <EmptyState onGoUpload={() => nav('/upload')} />
            ) : (
                <div className="space-y-2" data-testid="library-sections">
                    {sections.map((sec) => {
                        const itemCount =
                            (sec.bills?.length || 0) +
                            (sec.ledgers?.length || 0) +
                            (sec.expenses?.length || 0);
                        if (itemCount === 0) return null;
                        const spend = sec.bills?.reduce((s, b) => s + (Number(b.total) || 0), 0) || 0;
                        const chip =
                            view === 'vendor' && sec.key !== '__expenses__'
                                ? `${sec.bills.length} bills · ${fmtMoney(spend)}`
                                : view === 'month'
                                    ? `${sec.bills.length} bills · ${fmtMoney(spend)}`
                                    : null;
                        // Per-section download scope so users can grab
                        // ONE month or ONE vendor as either merged PDF or ZIP.
                        // General Expenses divider skips the download menu —
                        // expenses don't have attached files today.
                        const sectionScope =
                            sec.key === '__expenses__'
                                ? null
                                : view === 'vendor'
                                    ? { vendor_key: sec.key }
                                    : { month: sec.key };
                        return (
                            <DividerSection
                                key={sec.key}
                                id={sec.key}
                                title={sec.name}
                                chip={chip}
                                count={itemCount}
                                trailing={
                                    sectionScope && (sec.bills.length + sec.ledgers.length) > 0 ? (
                                        <ExportMenu
                                            exporting={exporting}
                                            onExport={(format) =>
                                                downloadExport({ format, scope: sectionScope })
                                            }
                                            label="Download"
                                            variant="ghost"
                                            size="xs"
                                            testidPrefix={`library-section-export-${sec.key}`}
                                        />
                                    ) : null
                                }
                            >
                                {sec.bills.map((b) => <BillCard key={`b-${b.id}`} bill={b} />)}
                                {sec.ledgers.map((l) => <LedgerCard key={`l-${l.id}`} ledger={l} />)}
                                {sec.expenses.map((e) => <ExpenseCard key={`e-${e.id}`} expense={e} />)}
                            </DividerSection>
                        );
                    })}
                </div>
            )}

            {/* Duplicate-cleanup dialog (Sprint 2026-09) — opens from the
                "Find duplicates" button in the header. When bills are
                deleted we reload the library so counts stay accurate. */}
            <DuplicatesDialog
                open={duplicatesOpen}
                onOpenChange={setDuplicatesOpen}
                onCleaned={load}
            />
        </div>
    );
}

// ---------------------------------------------------------------
// KindChip — small toggle at the top of the page
// ---------------------------------------------------------------
function KindChip({ label, icon: Icon, active, onClick, testid, count }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            className={
                'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ' +
                (active
                    ? 'bg-primary/10 border-primary/40 text-primary'
                    : 'bg-muted/40 border-border text-muted-foreground hover:bg-muted')
            }
            aria-pressed={active}
        >
            <Icon size={12} />
            {label}
            {typeof count === 'number' && (
                <span
                    className={
                        'ml-0.5 rounded-full px-1.5 py-[1px] text-[10px] tabular-nums ' +
                        (active ? 'bg-primary/20 text-primary' : 'bg-background text-muted-foreground')
                    }
                >
                    {count}
                </span>
            )}
        </button>
    );
}

// ---------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------
function LibrarySkeleton() {
    return (
        <div className="space-y-8">
            {[0, 1].map((i) => (
                <div key={i}>
                    <div className="flex items-center gap-3 mb-3">
                        <Skeleton className="h-5 w-40" />
                        <Skeleton className="h-4 w-20" />
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4">
                        {Array.from({ length: 6 }).map((_, j) => (
                            <div key={j} className="space-y-2">
                                <Skeleton className="aspect-[3/4] w-full rounded-lg" />
                                <Skeleton className="h-3 w-4/5" />
                                <Skeleton className="h-3 w-3/5" />
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

// ---------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------
function EmptyState({ onGoUpload }) {
    return (
        <Card className="p-10 text-center" data-testid="library-empty">
            <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-4">
                <FolderOpen size={30} strokeWidth={1.4} />
            </div>
            <h3 className="text-lg font-semibold mb-1">Your library is empty</h3>
            <p className="text-sm text-muted-foreground max-w-md mx-auto mb-4">
                Once you upload bills, log expenses, or attach vendor ledgers, they'll all show up here — organised like a physical file folder you can browse or print.
            </p>
            <Button onClick={onGoUpload} data-testid="library-empty-upload-btn">
                <ImageOff size={14} className="mr-1.5" />
                Upload your first bill
            </Button>
        </Card>
    );
}
